import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import StatisticsGlassCard from './StatisticsGlassCard';
import { DESIGN_TOKENS } from '../../constants/designTokens';
import StatisticsDesktopSectionHeader from './StatisticsDesktopSectionHeader';
import { STATS_ICONS } from './statisticsAssets';
import useChartContainerReady from './useChartContainerReady';

export default function StatisticsDesktopAttendanceTrendCard({
  attendanceStats,
  isAll,
  isEditMode,
  longPressHandlers,
}) {
  const trendColor = DESIGN_TOKENS.brand.primaryHex;
  const { containerRef, isReady } = useChartContainerReady();

  const lastRate = attendanceStats.length > 0 ? attendanceStats[attendanceStats.length - 1].rate : 0;

  return (
    <StatisticsGlassCard className="h-full p-6 flex flex-col" {...longPressHandlers} isEditMode={isEditMode}>
      <div className="flex items-start justify-between">
        <StatisticsDesktopSectionHeader
          iconSrc={STATS_ICONS.chart}
          title={isAll ? '부서별 출석 현황' : '출석 추이'}
          badge="TREND"
        />
        {lastRate > 0 && (
          <div className="flex flex-col items-end -mt-1">
            <span className="text-2xl font-black font-do-hyeon" style={{ color: trendColor }}>{lastRate}%</span>
            <span className="text-[10px] font-bold text-gray-400">최근 출석률</span>
          </div>
        )}
      </div>

      <div ref={containerRef} className="flex-1 min-h-[180px] min-w-0 mt-2">
        {attendanceStats.length > 0 ? (
          isReady ? (
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
              <AreaChart data={attendanceStats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="attendanceGradientPremium" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={trendColor} stopOpacity={0.35} />
                    <stop offset="50%" stopColor={trendColor} stopOpacity={0.12} />
                    <stop offset="100%" stopColor={trendColor} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fontWeight: 700, fill: '#9CA3AF' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fontWeight: 600, fill: '#D1D5DB' }}
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255,255,255,0.98)',
                    border: '1px solid #F3F4F6',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                    padding: '8px 12px',
                    fontSize: '11px',
                    fontWeight: 700,
                  }}
                  formatter={(value) => [`${value}%`, '출석률']}
                  labelFormatter={(label) => `${label}`}
                />
                <Area
                  type="monotone"
                  dataKey="rate"
                  stroke={trendColor}
                  strokeWidth={3}
                  fill="url(#attendanceGradientPremium)"
                  dot={{ fill: '#fff', strokeWidth: 3, r: 5, stroke: trendColor }}
                  activeDot={{ r: 8, fill: trendColor, stroke: '#fff', strokeWidth: 3 }}
                  animationDuration={1200}
                  animationEasing="ease-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : null
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 font-bold">
            <div className="text-center">
              <div className="text-3xl mb-2">📊</div>
              <p className="text-sm">출석 데이터가 없습니다</p>
            </div>
          </div>
        )}
      </div>
    </StatisticsGlassCard>
  );
}
